import { fixedUi } from '@/i18n/fixedUi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import SheinOpsPager from '@/shein-platform/components/SheinOpsPager';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

/**
 * 报名记录 · 完整 SKU 价格弹窗（列表页「价格摘要」只直显 2 行，其余在这里看全）。
 * 头部不展示商品标题，只展示 SPU / SKC / 货号；
 * 关闭方式：关闭按钮 / Escape / 点击弹窗外任意位置（含遮罩）。
 */
export default function SheinMarketingSkuPriceModal({
  open = false,
  spu = '',
  skc = '',
  supplierCode = '',
  rows = [],
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const panelRef = useRef(null);

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  useEffect(() => {
    if (!open) return undefined;
    setPage(1);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    /** 捕获阶段：点在弹窗面板外（遮罩 / 页面任意处）即关闭 */
    const onPointerDown = (e) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (panel.contains(e.target)) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = list.slice(start, start + pageSize);

  const metaBits = [
    { label: 'SPU', value: spu || '—' },
    { label: 'SKC', value: skc || '—' },
    { label: '货号', value: supplierCode || '—' },
  ];

  return createPortal(
    <div className="spo-batch-modal spo-marketing-sku-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="spo-batch-modal__panel spo-marketing-sku-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={fixedUi("SKU 价格明细")}
      >
        <div className="spo-batch-modal__head spo-marketing-sku-modal__head">
          <div>
            <div className="spo-marketing-sku-modal__meta">
              {metaBits.map((bit) => (
                <span key={bit.label} className="spo-marketing-sku-modal__meta-item">
                  <span className="spo-marketing-sku-modal__meta-label">{bit.label}</span>
                  <span className="spo-marketing-sku-modal__meta-value">{bit.value}</span>
                </span>
              ))}
            </div>
            <p className="spo-marketing-sku-modal__sub">{t('marketing.enSkuTotal', { count: total })}</p>
          </div>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="spo-batch-modal__body spo-marketing-sku-modal__body">
          {total === 0 ? (
            <p className="spo-marketing-sku-modal__empty">{fixedUi("暂无 SKU 价格信息")}</p>
          ) : (
            <>
              <table className="spo-marketing-sku-modal__table">
                <thead>
                  <tr>
                    <th scope="col">SKU</th>
                    <th scope="col">{fixedUi("价格")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, idx) => (
                    <tr key={row.key || `${safePage}-${idx}`}>
                      <td className="spo-marketing-sku-modal__sku">{row.sku}</td>
                      <td className="spo-marketing-sku-modal__price">{row.priceText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="spo-marketing-sku-modal__pager">
                <SheinOpsPager
                  page={safePage}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  ariaLabel={t('marketing.enSkuPager')}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
